import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import sellarModule from '../../../api/modules/seller.module';
import useDebounce from '../../../hooks/useDebounce';
import { getOrderStatusOptions } from '../../../utils/orderStatus';
import { parseRand } from '../../Dashboard/dashboard.parts';
import OrderDetailsDrawer from './OrderDetailsDrawer';
import { AllOrderView } from './AllOrder.view';

/**
 * Every order on the platform, the container. Mounted by `/admin-dashboard`;
 * the default export and its props are unchanged so that page was not touched.
 *
 * The query is untouched: same `['get-all-orders', {...}]` key (which the order
 * drawer's resubmit mutation invalidates by name), same `attention` flag
 * handling, same `buildParams`, same `getAllOrders` call, same fallback shape.
 *
 * The two derivations the columns used to do inline, the order total and the
 * "check three fields for a status" fallback, are done here so the view stays
 * pure and the explorer can hand it rows that already carry both.
 */
export default function AllOrderTable() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);

  const debouncedSearch = useDebounce(searchTerm, 500);

  const attentionMode = statusFilter === 'attention';

  const buildParams = (params) =>
    Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) =>
          value !== '' &&
          value !== null &&
          value !== undefined &&
          !(typeof value === 'number' && isNaN(value)),
      ),
    );

  const queryKey = useMemo(
    () => [
      'get-all-orders',
      {
        page: currentPage,
        limit: 100,
        search: debouncedSearch?.trim() || '',
        status: attentionMode ? '' : statusFilter || '',
        attention: attentionMode ? 'true' : '',
      },
    ],
    [currentPage, debouncedSearch, statusFilter, attentionMode],
  );

  const { data: queryData, isFetching, isError, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = buildParams(queryKey[1]);
      const { success, data: response, error } = await sellarModule.getAllOrders(params);
      if (!success) {
        // eslint-disable-next-line no-console
        console.error('Error fetching orders:', error);
        return { orders: [], page: 1, page_size: pageSize, total_pages: 0 };
      }
      return response || { orders: [], page: 1, page_size: pageSize, total_pages: 0 };
    },
    keepPreviousData: true,
  });

  const rows = useMemo(
    () =>
      (queryData?.orders || []).map((order) => ({
        ...order,
        orderTotal: order.payment
          ? parseRand(order.payment)
          : (order.items || []).reduce(
              (sum, item) => sum + parseRand(item.price) * (item.quantity || 1),
              0,
            ),
        derivedStatus:
          order.order_status || order.shipment_status || order.status || 'pending',
      })),
    [queryData],
  );

  return (
    <AllOrderView
      rows={rows}
      loading={isFetching}
      error={isError ? 'We could not reach the orders service.' : null}
      onRetry={refetch}
      search={searchTerm}
      onSearchChange={setSearchTerm}
      status={statusFilter}
      onStatusChange={setStatusFilter}
      statusOptions={getOrderStatusOptions()}
      needsAttentionCount={queryData?.needs_attention_count || 0}
      onShowAttention={() => {
        setStatusFilter('attention');
        setCurrentPage(1);
      }}
      page={queryData?.page || 1}
      pageCount={queryData?.total_pages || 0}
      pageSize={queryData?.page_size || pageSize}
      total={queryData?.total ?? rows.length}
      onPageChange={setCurrentPage}
      onView={(order) => {
        setSelectedOrder(order);
        setOpenDrawer(true);
      }}
      drawer={
        selectedOrder ? (
          <OrderDetailsDrawer
            isOpen={openDrawer}
            onClose={() => setOpenDrawer(false)}
            order={selectedOrder}
            seller
          />
        ) : null
      }
    />
  );
}
